import { useState, useEffect } from 'react';
import {
    Box, Heading, Text, Button, VStack, HStack, Card, CardBody,
    Stat, StatLabel, StatNumber, StatHelpText,
    Table, Thead, Tbody, Tr, Th, Td, Badge,
    useToast, Container, Textarea, Modal, ModalOverlay, ModalContent,
    ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure,
    Spinner, Center, Icon, TableContainer
} from '@chakra-ui/react';
import { FiClock, FiCheckCircle, FiLogOut, FiCalendar } from 'react-icons/fi';
import { formatDateTime, formatTime, formatDate, getNow } from '../utils/dateUtils';
import { authAPI } from '../services/api';

const TeacherAttendancePage = () => {
    const [status, setStatus] = useState(null); // { is_checked_in, last_check_in, ... }
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentTime, setCurrentTime] = useState(getNow());
    const [note, setNote] = useState('');

    // Check In/Out Modal
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [actionType, setActionType] = useState('check-in'); // 'check-in' | 'check-out'
    const toast = useToast();

    // Live Clock
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(getNow()), 1000);
        return () => clearInterval(timer);
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [statusRes, historyRes] = await Promise.all([
                authAPI.attendance.getStatus(),
                authAPI.attendance.getHistory({ range: 30 })
            ]);
            setStatus(statusRes.data);
            setHistory(historyRes.data);
        } catch (error) {
            console.error(error);
            toast({ title: "無法取得出勤資料", status: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleActionClick = (type) => {
        setActionType(type);
        setNote('');
        onOpen();
    };

    const handleSubmit = async () => {
        try {
            if (actionType === 'check-in') {
                await authAPI.attendance.checkIn(note);
                toast({ title: "上班打卡成功！", status: "success" });
            } else {
                await authAPI.attendance.checkOut(note);
                toast({ title: "下班打卡成功！", status: "success" });
            }
            onClose();
            fetchData(); // Refresh data
        } catch (error) {
            toast({
                title: "打卡失敗",
                description: error.response?.data?.detail || "請稍後再試",
                status: "error"
            });
        }
    };

    if (loading) return <Center h="200px"><Spinner /></Center>;

    const isWorking = status?.is_checked_in;

    return (
        <Box w="100%" h="100%" overflow="hidden">
            <Container maxW="container.lg" py={2} h="100%" display="flex" flexDirection="column">
                <Heading mb={4} size="lg" color="brand.800">我的出勤紀錄</Heading>

                {/* Top Section: Clock & Action - Horizontal Scroll Area */}
                <Box
                    w="100%"
                    overflowX="scroll"
                    pb={2}
                    mb={4}
                    flexShrink={0}
                    sx={{
                        '-webkit-overflow-scrolling': 'touch'
                    }}
                    css={{
                        '&::-webkit-scrollbar': {
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#888',
                            borderRadius: '4px',
                        },
                    }}
                >
                    <HStack spacing={6} alignItems="stretch" direction="row" h="280px" minW="800px">
                        {/* 1. Status & Action Card (Now on Left) */}
                        <Card flex={1} align="center" justify="center" border="2px solid" borderColor={isWorking ? "green.400" : "gray.200"}>
                            <CardBody display="flex" flexDirection="column" alignItems="center" justifyContent="center" w="100%">
                                <Text fontSize="lg" color="gray.500" mb={2}>目前狀態</Text>
                                <Badge
                                    colorScheme={isWorking ? "green" : "gray"}
                                    fontSize="2xl"
                                    px={4} py={1}
                                    borderRadius="full"
                                    mb={6}
                                >
                                    {isWorking ? "工作中 ON DUTY" : "非值勤 OFF DUTY"}
                                </Badge>

                                {!isWorking ? (
                                    <Button
                                        colorScheme="green"
                                        size="lg"
                                        h="80px" w="200px"
                                        fontSize="2xl"
                                        leftIcon={<FiCheckCircle />}
                                        onClick={() => handleActionClick('check-in')}
                                        boxShadow="lg"
                                    >
                                        上班打卡
                                    </Button>
                                ) : (
                                    <Button
                                        colorScheme="red"
                                        variant="outline"
                                        size="lg"
                                        h="80px" w="200px"
                                        fontSize="2xl"
                                        leftIcon={<FiLogOut />}
                                        onClick={() => handleActionClick('check-out')}
                                        boxShadow="sm"
                                        _hover={{ bg: 'red.50' }}
                                    >
                                        下班打卡
                                    </Button>
                                )}

                                {isWorking && status?.last_check_in && (
                                    <Text mt={3} color="gray.500" fontSize="sm">
                                        上班時間: {formatTime(status.last_check_in)}
                                    </Text>
                                )}
                            </CardBody>
                        </Card>

                        {/* 2. Live Clock Card (Now on Right) */}
                        <Card flex={1} bg="brand.500" color="white" align="center" justify="center">
                            <CardBody display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                                <Icon as={FiClock} w={10} h={10} mb={2} />
                                <Text fontSize="lg" fontWeight="medium">現在時間</Text>
                                <Heading size="2xl" my={2}>
                                    {formatTime(currentTime)}:{currentTime.getSeconds().toString().padStart(2, '0')}
                                </Heading>
                                <Text fontSize="md">
                                    {formatDate(currentTime)}
                                </Text>
                            </CardBody>
                        </Card>
                    </HStack>
                </Box>

                {/* History Table - Fill remaining height with internal scroll */}
                <Card flex="1" overflow="hidden">
                    <CardBody display="flex" flexDirection="column" h="100%">
                        <Heading size="md" mb={4} display="flex" alignItems="center" flexShrink={0}>
                            <Icon as={FiCalendar} mr={2} color="brand.500" />
                            最近出勤紀錄 (30天)
                        </Heading>

                        <TableContainer overflow="auto" flex="1" w="100%" maxW={{ base: 'calc(100vw - 32px)', md: '100%' }}>
                            <Table variant="simple" minW="600px" whiteSpace="nowrap">
                                <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                                    <Tr>
                                        <Th>日期</Th>
                                        <Th>上班時間</Th>
                                        <Th>下班時間</Th>
                                        <Th isNumeric>工時 (分鐘)</Th>
                                        <Th>備註</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {history.map(log => (
                                        <Tr key={log.id}>
                                            <Td>{formatDate(log.check_in_time)}</Td>
                                            <Td fontWeight="bold" color="green.600">
                                                {formatTime(log.check_in_time)}
                                            </Td>
                                            <Td>
                                                {log.check_out_time ? (
                                                    <Text fontWeight="bold" color="red.600">
                                                        {formatTime(log.check_out_time)}
                                                    </Text>
                                                ) : (
                                                    <Badge colorScheme="green">工作中</Badge>
                                                )}
                                            </Td>
                                            <Td isNumeric>
                                                {log.duration_minutes ? (
                                                    <Text>{log.duration_minutes} min</Text>
                                                ) : "-"}
                                            </Td>
                                            <Td color="gray.500" fontSize="sm">{log.note || '-'}</Td>
                                        </Tr>
                                    ))}
                                    {history.length === 0 && (
                                        <Tr>
                                            <Td colSpan={5} textAlign="center">尚無紀錄</Td>
                                        </Tr>
                                    )}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </CardBody>
                </Card>

                {/* Modal for Note */}
                <Modal isOpen={isOpen} onClose={onClose} isCentered>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>
                            {actionType === 'check-in' ? "上班打卡確認" : "下班打卡確認"}
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <Text mb={2}>現在時間：{formatDateTime(currentTime)}</Text>
                            <Text mb={4} fontSize="sm" color="gray.500">
                                {actionType === 'check-in'
                                    ? "新的一天開始了，祝您工作順利！"
                                    : "辛苦了！確認下班前可以寫個備註。"}
                            </Text>

                            <Textarea
                                placeholder="選填：備註 (例如：今天提早到、補休時數...)"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                            />
                        </ModalBody>
                        <ModalFooter>
                            <Button onClick={onClose} mr={3}>取消</Button>
                            <Button
                                colorScheme={actionType === 'check-in' ? "green" : "red"}
                                onClick={handleSubmit}
                            >
                                確認打卡
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </Container>
        </Box>
    );
};

export default TeacherAttendancePage;
